import { useState } from "react";
import { Link } from "react-router-dom";
import { useClerk } from "@clerk/react";
import { toast } from "sonner";
import { Trash, ShieldCheck, FileText, DownloadSimple } from "@phosphor-icons/react";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { useApi, errText } from "@/lib/api";

export const DownloadData = ({ testid = "download-data-btn" }) => {
  const api = useApi();
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      const data = await api.get("/account/export");
      const a = Object.assign(document.createElement("a"), {
        href: URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" })),
        download: `calibrunway-data-${new Date().toISOString().slice(0, 10)}.json`,
      });
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Your data file is downloading");
    } catch (e) { toast.error(errText(e, "Couldn't prepare your data")); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={testid} onClick={run} disabled={busy}
      className="inline-flex items-center gap-2 rounded-full border border-[#2A2A2A] px-4 py-2 text-xs hover:border-white transition-colors disabled:opacity-50">
      <DownloadSimple size={13} /> {busy ? "Preparing…" : "Download my data"}
    </button>
  );
};

export const DeleteAccount = ({ member }) => {
  const api = useApi();
  const { signOut } = useClerk();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const run = async () => {
    setBusy(true);
    try {
      await api.delete("/account", { confirm: "DELETE" });
      toast.success("Your account and data have been deleted");
      await signOut().catch(() => {});
      window.location.replace("/");
    } catch (e) { toast.error(errText(e, "Couldn't delete your account")); setBusy(false); }
  };
  return (
    <section data-testid="delete-account-section" className="rounded-xl border border-[#FF5C7A]/30 bg-[#121212] p-6">
      <h2 className="font-display text-xl flex items-center gap-2"><Trash className="text-[#FF5C7A]" /> Delete account</h2>
      <p className="mt-2 text-sm text-[#A3A3A3] max-w-2xl">{member
        ? "Deletes your login and your private Calibrator chats, and removes you from the team. The team's workspace belongs to its owner and isn't touched. This can't be undone."
        : "Cancels your subscription, deletes your login, disconnects your bank, QuickBooks and Stripe, and erases all your forecasts, scenarios, chats, pipeline, reports and investor lists. Teammates lose access. This can't be undone."}</p>
      <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); setText(""); }}>
        <AlertDialogTrigger asChild>
          <button data-testid="delete-account-btn" className="mt-5 rounded-full border border-[#FF5C7A]/60 px-5 py-2.5 text-sm text-[#FF8FA3] hover:bg-[#FF5C7A]/10 transition-colors">Delete my account and data</button>
        </AlertDialogTrigger>
        <AlertDialogContent data-testid="delete-account-dialog" className="bg-[#121212] border-[#2A2A2A] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete your account for good?</AlertDialogTitle>
            <AlertDialogDescription className="text-[#A3A3A3]">Everything is erased right away, and backup copies are gone within 30 days. You'll get a confirmation email. Download your data first if you want a copy, then type DELETE to confirm.</AlertDialogDescription>
          </AlertDialogHeader>
          <div><DownloadData testid="delete-dialog-download-btn" /></div>
          <input data-testid="delete-account-confirm-input" value={text} onChange={(e) => setText(e.target.value)} placeholder="DELETE" autoComplete="off"
            className="rounded-lg bg-[#050505] border border-[#2A2A2A] px-3 py-2 font-mono text-sm focus:outline-none focus:ring-2 focus:ring-[#FF5C7A]" />
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-account-cancel" className="bg-transparent border-[#2A2A2A] text-white hover:bg-[#1A1A1A] hover:text-white">Keep my account</AlertDialogCancel>
            <button data-testid="delete-account-confirm" disabled={text !== "DELETE" || busy} onClick={run}
              className="rounded-md bg-[#FF5C7A] px-4 py-2 text-sm font-semibold text-[#050505] disabled:opacity-40">{busy ? "Deleting…" : "Delete everything"}</button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
};

export const PrivacySummary = () => (
  <section data-testid="privacy-summary" className="rounded-xl border border-[#2A2A2A] bg-[#121212] p-6">
    <h2 className="font-display text-xl flex items-center gap-2"><ShieldCheck className="text-[#00FF9D]" /> Your data</h2>
    <ul className="mt-3 space-y-2 text-sm text-[#A3A3A3] list-disc pl-5 max-w-2xl">
      <li>We never see your bank or QuickBooks passwords, and integration tokens are stored encrypted.</li>
      <li>We don't sell your data or use it for ads.</li>
      <li>Disconnecting an integration on the Integrations page deletes its access token straight away.</li>
      <li>You can download everything we hold about you (forecasts, scenarios, chats, pipeline, reports and settings) as one data file at any time.</li>
    </ul>
    <div className="mt-4 flex flex-wrap items-center gap-4">
      <DownloadData />
      <Link to="/terms#privacy" data-testid="privacy-policy-link" className="inline-flex items-center gap-2 text-sm text-[#00FF9D] hover:underline"><FileText size={14} /> Read the Privacy Policy and data deletion terms</Link>
    </div>
  </section>
);
